import { useState } from 'react';
import { Spinner, Input, Button } from '@klaro7/ui';
import { isValidIban } from '@klaro7/validators';
import type { ConvertedUserRow } from '@klaro7/shared-types';

import { useMyProfessionalProfile, useSetMyIban } from '@/features/professional-profile/hooks/useProfessionalProfile';
import { useMySummary, useMyConversions } from './api';
import { PayoutsSection } from './PayoutsSection';

function formatEur(amount: number): string {
  return amount.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  });
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Group an IBAN in blocks of 4 for display only. */
function formatIbanDisplay(iban: string): string {
  return iban.replace(/(.{4})/g, '$1 ').trim();
}

/** Strip spaces and uppercase — what we store/validate. */
function normalizeIban(value: string): string {
  return value.replace(/\s+/g, '').toUpperCase();
}

interface SummaryCardProps {
  label: string;
  value: string;
  sub?: string;
}

function SummaryCard({ label, value, sub }: SummaryCardProps) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6">
      <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
        {label}
      </p>
      <p className="mt-2 text-3xl font-bold text-neutral-900">{value}</p>
      {sub && <p className="mt-1 text-sm text-neutral-500">{sub}</p>}
    </div>
  );
}

/** Read-only, locked display of the saved IBAN. */
function IbanLockedCard({ iban }: { iban: string }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
            IBAN per i pagamenti
          </p>
          <p className="mt-2 font-mono text-lg font-semibold tracking-wide text-neutral-900">
            {formatIbanDisplay(iban)}
          </p>
        </div>
        <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75M3.75 21.75h16.5a1.5 1.5 0 001.5-1.5v-7.5a1.5 1.5 0 00-1.5-1.5H3.75a1.5 1.5 0 00-1.5 1.5v7.5a1.5 1.5 0 001.5 1.5z" />
          </svg>
          Confermato
        </span>
      </div>
      <p className="mt-2 text-xs text-neutral-400">
        L'IBAN è stato confermato e non può essere modificato. Per una variazione contatta il supporto.
      </p>
    </div>
  );
}

/** Required IBAN setup: insert + confirm, with a permanence warning. */
function IbanSetupCard() {
  const setIban = useSetMyIban();
  const [iban, setIbanValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalized = normalizeIban(iban);
  const normalizedConfirm = normalizeIban(confirm);

  const handleSubmit = () => {
    setError(null);
    if (normalized !== normalizedConfirm) {
      setError('I due IBAN non corrispondono.');
      return;
    }
    if (!isValidIban(normalized)) {
      setError('IBAN non valido. Controlla che sia corretto.');
      return;
    }
    setIban.mutate(normalized, {
      onError: (err: unknown) => {
        const apiMsg = (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message;
        setError(apiMsg || 'Impossibile salvare l\'IBAN. Riprova.');
      },
    });
  };

  const canSubmit = normalized.length > 0 && normalizedConfirm.length > 0 && accepted && !setIban.isPending;

  return (
    <div className="w-full max-w-xl rounded-2xl border border-neutral-200 bg-white p-6 shadow-xl shadow-neutral-300/40 sm:p-8">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-teal/10 text-brand-teal">
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 10.5h15M5.25 21V10.5M18.75 21V10.5M12 3.75L20.25 9H3.75L12 3.75z" />
          </svg>
        </div>
        <div>
          <h2 className="text-lg font-bold text-neutral-900">Inserisci il tuo IBAN</h2>
          <p className="text-sm text-neutral-500">
            Necessario per ricevere i pagamenti. Inseriscilo e confermalo.
          </p>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        <Input
          label="IBAN"
          value={iban}
          onChange={(e) => setIbanValue(normalizeIban(e.target.value))}
          placeholder="IT60X0542811101000000123456"
          autoComplete="off"
          spellCheck={false}
          className="font-mono tracking-wide"
        />
        <Input
          label="Conferma IBAN"
          value={confirm}
          onChange={(e) => setConfirm(normalizeIban(e.target.value))}
          onPaste={(e) => e.preventDefault()}
          placeholder="Reinserisci l'IBAN"
          autoComplete="off"
          spellCheck={false}
          className="font-mono tracking-wide"
        />

        <label className="flex items-start gap-2.5 rounded-lg bg-amber-50 p-3 text-[13px] text-amber-800 ring-1 ring-amber-200">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-amber-300 text-brand-teal focus:ring-brand-teal"
          />
          <span>
            Confermo che l'IBAN inserito è corretto. <strong>Una volta salvato non potrà più essere modificato.</strong>
          </span>
        </label>

        {error && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
        )}

        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          loading={setIban.isPending}
          className="w-full bg-brand-orange hover:bg-brand-orange/90"
        >
          Salva e conferma IBAN
        </Button>
      </div>
    </div>
  );
}

/** List of users converted (first recharge within 12h of a call) crediting a €7 bonus. */
function ConvertedUsersSection({ conversions }: { conversions: ConvertedUserRow[] | undefined }) {
  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-6">
      <h2 className="text-lg font-semibold text-neutral-900">Utenti convertiti</h2>
      <p className="mt-1 text-sm text-neutral-500">
        Utenti che hanno ricaricato entro 12 ore dalla tua chiamata con bonus di benvenuto.
        Ogni conversione vale €7 di compenso.
      </p>
      {!conversions?.length ? (
        <p className="mt-4 text-sm text-neutral-500">Nessun utente convertito finora.</p>
      ) : (
        <ul className="mt-4 divide-y divide-neutral-100">
          {conversions.map((c) => (
            <li key={c.earningId} className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-neutral-900">{c.convertedUserName}</p>
                <p className="text-xs text-neutral-500">{formatDate(c.createdAt)}</p>
              </div>
              <span className="font-semibold text-brand-teal">{formatEur(c.amount)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function CompensiPage() {
  const { data: profile, isLoading: profileLoading } = useMyProfessionalProfile();
  const { data: summary, isLoading, isError } = useMySummary();
  const { data: conversions } = useMyConversions();

  // The /professionals/me payload is the flat professional document.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const prof = (profile as any)?.professional ?? profile;
  const iban: string | undefined = prof?.iban;
  const hasIban = !!iban;

  const summaryContent = isLoading ? (
    <div className="flex justify-center py-16">
      <Spinner size="lg" />
    </div>
  ) : isError ? (
    <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
      Impossibile caricare il riepilogo dei compensi. Riprova più tardi.
    </div>
  ) : !summary ? (
    <div className="flex flex-col items-center justify-center rounded-xl border border-neutral-200 bg-white py-16 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-neutral-100">
        <svg className="h-6 w-6 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
        </svg>
      </div>
      <p className="mt-4 font-medium text-neutral-700">Nessun compenso disponibile</p>
      <p className="mt-1 text-sm text-neutral-500">
        I tuoi compensi appariranno qui non appena saranno registrati
      </p>
    </div>
  ) : (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <SummaryCard
          label="In attesa di pagamento"
          value={formatEur(summary.pendingTotal)}
          sub={
            summary.pendingCount > 0
              ? `${summary.pendingCount} ${summary.pendingCount === 1 ? 'compenso' : 'compensi'} · più vecchio: ${formatDate(summary.oldestPendingAt)}${
                  summary.allocatedTotal
                    ? ` · ${formatEur(summary.allocatedTotal)} già in un documento qui sotto`
                    : ''
                }`
              : 'Nessun compenso in sospeso'
          }
        />
        <SummaryCard label="Pagato" value={formatEur(summary.paidTotal)} />
        <SummaryCard label="Compensi in sospeso" value={String(summary.pendingCount)} />
      </div>

      {summary.pendingCount === 0 && summary.paidTotal === 0 && (
        <div className="rounded-xl border border-neutral-200 bg-white px-5 py-4 text-sm text-neutral-600">
          Non hai ancora compensi registrati. Inizia a lavorare con i tuoi clienti per vedere i guadagni qui.
        </div>
      )}
    </>
  );

  // Documents + confirmation live here (not on Profilo fiscale): this is the page
  // consultants open when they look for their money.
  const pageBody = (
    <div className="space-y-6">
      {summaryContent}
      <PayoutsSection />
      <ConvertedUsersSection conversions={conversions} />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Compensi</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Riepilogo dei tuoi guadagni e dei compensi in sospeso
        </p>
      </div>

      {profileLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : hasIban ? (
        <div className="space-y-6">
          <IbanLockedCard iban={iban!} />
          {pageBody}
        </div>
      ) : (
        <div className="relative">
          {/* Rest of the page is greyed-out / blurred until an IBAN is set */}
          <div aria-hidden className="pointer-events-none select-none space-y-6 opacity-50 blur-sm grayscale">
            {pageBody}
          </div>
          <div className="absolute inset-0 flex items-start justify-center px-4 pt-6 sm:pt-10">
            <IbanSetupCard />
          </div>
        </div>
      )}
    </div>
  );
}
